import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getClubTeams,
  createTeam,
  createSession,
  getTeamAthletes,
  getTeamSessions,
  getClubLocations,
  type Athlete,
  type ClubLocation,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  createInvitation,
  getSentInvitations,
  cancelInvitation,
  type Invitation,
} from "../../services/invitations";
import {
  getTeamGroups,
  createGroup,
  deleteGroup,
  addGroupMember,
  removeGroupMember,
  type Group,
} from "../../services/groups";
import PresenceCatalog from "../../components/coach/PresenceCatalog";
import { formatDate } from "../../lib/dateFormat";
import DateInput from "../../components/DateInput";

type Team = { id: string; name: string; age_group?: string | null; sport_type?: string | null };
type SessionItem = { id: string; title: string; start_at: string; end_at: string; type: "sport" | "educational" };

type TabId = "attendance" | "sessions" | "athletes" | "teams" | "groups";

export default function Antrenor() {
  const { t } = useTranslation();
  const {
    clubs: ctxClubs,
    activeClubId: ctxClubId,
    setActiveClubId: setCtxClubId,
  } = useAuth();

  // Local selectors
  const [clubId, setClubId] = useState<string | null>(ctxClubId);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("attendance");

  // Session form
  const [sessForm, setSessForm] = useState({ title: "", type: "sport", start: "", end: "", location: "", location_id: "" });
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [locations, setLocations] = useState<ClubLocation[]>([]);
  const [sessionError, setSessionError] = useState<string | null>(null);

  // Team creation form
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamForm, setTeamForm] = useState({ name: "", age_group: "", sport_type: "" });

  // Loading states
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);

  // Invitation states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);

  // Group states
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);

  // Sync local clubId with context
  useEffect(() => {
    if (ctxClubId) {
      setClubId(ctxClubId);
      return;
    }
    if (!ctxClubId && ctxClubs.length > 0) {
      setCtxClubId(ctxClubs[0].id);
      setClubId(ctxClubs[0].id);
    }
  }, [ctxClubId, ctxClubs, setCtxClubId]);

  // Load teams and locations when club changes
  useEffect(() => {
    (async () => {
      if (!clubId) return;
      setLoadingTeams(true);
      try {
        const [ts, locs] = await Promise.all([
          getClubTeams(clubId),
          getClubLocations(clubId),
        ]);
        setTeams(ts);
        setTeamId(ts[0]?.id || null);
        setLocations(locs.filter(l => l.is_active));
      } finally {
        setLoadingTeams(false);
      }
    })();
  }, [clubId]);

  // Load athletes, sessions, groups when team changes
  useEffect(() => {
    (async () => {
      if (!teamId) {
        setAthletes([]);
        setSessions([]);
        setGroups([]);
        return;
      }
      setLoadingData(true);
      try {
        const [athletesPage, ss, grps] = await Promise.all([
          getTeamAthletes(teamId),
          getTeamSessions(teamId),
          getTeamGroups(teamId),
        ]);
        const list = athletesPage?.data ?? [];
        setAthletes(list);
        setSessions(ss);
        setGroups(grps);
      } finally {
        setLoadingData(false);
      }
    })();
  }, [teamId]);

  const activeTeam = teams.find(t => t.id === teamId);

  // Create team handler
  async function handleCreateTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !teamForm.name.trim()) return;
    setSaving(true);
    try {
      await createTeam(clubId, {
        name: teamForm.name.trim(),
        age_group: teamForm.age_group.trim() || undefined,
        sport_type: teamForm.sport_type.trim() || undefined,
      });
      setTeamForm({ name: "", age_group: "", sport_type: "" });
      setShowCreateTeam(false);
      // Reload teams
      const ts = await getClubTeams(clubId);
      setTeams(ts);
    } catch (error: any) {
      console.error("Failed to create team:", error);
      alert(error?.response?.data?.error || "Failed to create team");
    } finally {
      setSaving(false);
    }
  }

  // Create session handler
  async function handleCreateSession(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId || !sessForm.title || !sessForm.start || !sessForm.end) return;
    setSaving(true);
    setSessionError(null);
    try {
      await createSession({
        team_id: teamId,
        type: sessForm.type as "sport" | "educational",
        title: sessForm.title,
        start_at: new Date(sessForm.start).toISOString(),
        end_at: new Date(sessForm.end).toISOString(),
        location_text: sessForm.location || undefined,
        location_id: sessForm.location_id || undefined,
      });
      const ss = await getTeamSessions(teamId);
      setSessions(ss);
      setSessForm({ title: "", type: "sport", start: "", end: "", location: "", location_id: "" });
    } catch (error: any) {
      console.error("Failed to create session:", error);
      // Check for location conflict error (409)
      if (error?.response?.status === 409) {
        setSessionError(error?.response?.data?.message || t("coach.locationConflict", "This location is already booked at this time"));
      } else {
        setSessionError(error?.response?.data?.error || t("coach.sessionCreateFailed", "Failed to create session"));
      }
    } finally {
      setSaving(false);
    }
  }

  // Invitation handlers
  async function loadInvitations() {
    try {
      const data = await getSentInvitations();
      setInvitations(data);
    } catch (error) {
      console.error("Failed to load invitations:", error);
    }
  }

  async function handleSendInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !teamId || !inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      await createInvitation({
        email: inviteEmail.trim(),
        role_code: "athlete",
        club_id: clubId,
        team_id: teamId,
      });
      setInviteEmail("");
      await loadInvitations();
    } catch (error: any) {
      console.error("Failed to send invitation:", error);
      alert(error?.response?.data?.error || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleCancelInvite(invitationId: string) {
    if (!window.confirm("Are you sure you want to cancel this invitation?")) return;
    try {
      await cancelInvitation(invitationId);
      await loadInvitations();
    } catch (error) {
      console.error("Failed to cancel invitation:", error);
    }
  }

  function openInviteModal() {
    setShowInviteModal(true);
    loadInvitations();
  }

  // Group handlers
  async function loadGroups() {
    if (!teamId) return;
    try {
      const data = await getTeamGroups(teamId);
      setGroups(data);
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  }

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault();
    if (!teamId || !groupForm.name.trim()) return;
    setSaving(true);
    try {
      await createGroup({
        team_id: teamId,
        name: groupForm.name.trim(),
        description: groupForm.description.trim() || undefined,
      });
      setGroupForm({ name: "", description: "" });
      await loadGroups();
      setShowGroupModal(false);
    } catch (error) {
      console.error("Failed to create group:", error);
      alert("Failed to create group");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteGroup(groupId: string) {
    if (!window.confirm("Are you sure you want to delete this group?")) return;
    try {
      await deleteGroup(groupId);
      await loadGroups();
    } catch (error) {
      console.error("Failed to delete group:", error);
    }
  }

  function openManageMembersModal(group: Group) {
    setSelectedGroup(group);
    setShowManageMembersModal(true);
  }

  async function handleAddMember(groupId: string, athleteId: string) {
    try {
      await addGroupMember(groupId, athleteId);
      await loadGroups();
      if (selectedGroup && selectedGroup.id === groupId) {
        const updated = await getTeamGroups(teamId!);
        const updatedGroup = updated.find((g) => g.id === groupId);
        if (updatedGroup) setSelectedGroup(updatedGroup);
      }
    } catch (error: any) {
      console.error("Failed to add member:", error);
      alert(error?.response?.data?.error || "Failed to add member");
    }
  }

  async function handleRemoveMember(groupId: string, athleteId: string) {
    if (!window.confirm("Remove this athlete from the group?")) return;
    try {
      await removeGroupMember(groupId, athleteId);
      await loadGroups();
      if (selectedGroup && selectedGroup.id === groupId) {
        const updated = await getTeamGroups(teamId!);
        const updatedGroup = updated.find((g) => g.id === groupId);
        if (updatedGroup) setSelectedGroup(updatedGroup);
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
    }
  }

  const teamInvitations = invitations.filter(inv => inv.role_code === "athlete" && inv.team_id === teamId);

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    {
      id: "attendance",
      label: t("coach.attendance", "Attendance"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    },
    {
      id: "sessions",
      label: t("coach.sessions", "Sessions"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: "athletes",
      label: t("coach.athletes", "Athletes"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: "teams",
      label: t("coach.teams", "Teams"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      id: "groups",
      label: t("coach.groups", "Groups"),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            {t("dash.coach", "Coach Dashboard")}
          </h1>
          <p className="text-slate-400 mt-1">
            {t("coach.subtitle", "Manage your teams, sessions, and athletes")}
          </p>
        </div>

        {/* Team Selector */}
        <div className="flex items-center gap-3">
          <select
            value={teamId || ""}
            onChange={(e) => setTeamId(e.target.value || null)}
            className="px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
            disabled={loadingTeams || !teams.length}
          >
            <option value="" disabled>{t("common.selectTeam", "Select Team")}</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {(loadingTeams || loadingData) && (
            <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {teamId && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("coach.athletes", "Athletes")}</p>
                <p className="text-xl font-bold text-white">{athletes.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("coach.sessions", "Sessions")}</p>
                <p className="text-xl font-bold text-white">{sessions.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("coach.groups", "Groups")}</p>
                <p className="text-xl font-bold text-white">{groups.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("coach.team", "Team")}</p>
                <p className="text-lg font-bold text-white truncate max-w-[100px]">{activeTeam?.name || "-"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-1.5">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Attendance Tab - Uses PresenceCatalog */}
        {activeTab === "attendance" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            {teamId && clubId ? (
              <PresenceCatalog teamId={teamId} clubId={clubId} />
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <p className="text-slate-400">{t("coach.selectTeamFirst", "Select a team to track attendance")}</p>
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === "sessions" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create Session Form */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("coach.createSession", "Create Session")}</h2>
              </div>

              <form onSubmit={handleCreateSession} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.title", "Title")} *</label>
                  <input
                    type="text"
                    required
                    placeholder={t("coach.sessionTitle", "Session title")}
                    value={sessForm.title}
                    onChange={(e) => setSessForm({ ...sessForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.type", "Type")}</label>
                    <select
                      value={sessForm.type}
                      onChange={(e) => setSessForm({ ...sessForm, type: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    >
                      <option value="sport">{t("session.sport", "Sport")}</option>
                      <option value="educational">{t("session.educational", "Educational")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.location", "Location")}</label>
                    {locations.length > 0 ? (
                      <select
                        value={sessForm.location_id}
                        onChange={(e) => setSessForm({ ...sessForm, location_id: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      >
                        <option value="">{t("coach.selectLocation", "Select location")}</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        placeholder={t("common.location", "Location")}
                        value={sessForm.location}
                        onChange={(e) => setSessForm({ ...sessForm, location: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {sessionError && (
                  <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-xl flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-red-300">{sessionError}</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.start", "Start")} *</label>
                    <DateInput mode="datetime"
                      required
                      value={sessForm.start}
                      onChange={(v) => setSessForm({ ...sessForm, start: v })}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.end", "End")} *</label>
                    <DateInput mode="datetime"
                      required
                      value={sessForm.end}
                      onChange={(v) => setSessForm({ ...sessForm, end: v })}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving || !teamId || !sessForm.title || !sessForm.start || !sessForm.end}
                  className="w-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {t("common.saving", "Saving...")}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("coach.createSession", "Create Session")}
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Sessions List */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-white">{t("coach.upcomingSessions", "Sessions")}</h2>
                </div>
                <span className="text-sm text-slate-400">{sessions.length} {t("common.total", "total")}</span>
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {sessions.length > 0 ? (
                  sessions.map((session) => (
                    <div key={session.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-medium text-white">{session.title}</h4>
                          <div className="flex items-center gap-3 mt-2 text-sm text-slate-400">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              session.type === "sport" ? "bg-emerald-500/20 text-emerald-300" : "bg-blue-500/20 text-blue-300"
                            }`}>
                              {session.type}
                            </span>
                            <span>{formatDate(new Date(session.start_at))}</span>
                            <span>{new Date(session.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 mx-auto mb-3 bg-slate-700/50 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-400 text-sm">{t("coach.noSessions", "No sessions yet")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Athletes Tab */}
        {activeTab === "athletes" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("coach.teamAthletes", "Team Athletes")}</h2>
              </div>
              <button
                onClick={openInviteModal}
                disabled={!teamId}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                {t("invite.inviteAthlete", "Invite Athlete")}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {athletes.length > 0 ? (
                athletes.map((athlete) => (
                  <div key={athlete.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-slate-600/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white font-bold">
                        {(athlete.full_name || athlete.email).substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white truncate">{athlete.full_name || athlete.email}</h4>
                        <p className="text-sm text-slate-400 truncate">{athlete.email}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-slate-400">{t("coach.noAthletes", "No athletes in this team")}</p>
                  <button
                    onClick={openInviteModal}
                    disabled={!teamId}
                    className="mt-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                  >
                    {t("invite.inviteFirst", "Invite your first athlete")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Teams Tab */}
        {activeTab === "teams" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("coach.myTeams", "My Teams")}</h2>
              </div>
              <button
                onClick={() => setShowCreateTeam(true)}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t("coach.createTeam", "Create Team")}
              </button>
            </div>

            {/* Create Team Form (inline) */}
            {showCreateTeam && (
              <div className="mb-6 p-4 bg-slate-900/50 rounded-xl border border-emerald-500/50">
                <form onSubmit={handleCreateTeam} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t("common.name", "Name")} *</label>
                      <input
                        type="text"
                        required
                        value={teamForm.name}
                        onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
                        placeholder={t("team.namePlaceholder", "Team name")}
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t("team.ageGroup", "Age Group")}</label>
                      <input
                        type="text"
                        value={teamForm.age_group}
                        onChange={(e) => setTeamForm({ ...teamForm, age_group: e.target.value })}
                        placeholder="e.g., U15"
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">{t("team.sportType", "Sport Type")}</label>
                      <input
                        type="text"
                        value={teamForm.sport_type}
                        onChange={(e) => setTeamForm({ ...teamForm, sport_type: e.target.value })}
                        placeholder="e.g., Football"
                        className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateTeam(false);
                        setTeamForm({ name: "", age_group: "", sport_type: "" });
                      }}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
                    >
                      {t("common.cancel", "Cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={saving || !teamForm.name.trim()}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                      {t("common.create", "Create")}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.length > 0 ? (
                teams.map((team) => (
                  <div
                    key={team.id}
                    onClick={() => setTeamId(team.id)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      team.id === teamId
                        ? "bg-emerald-500/20 border-emerald-500/50"
                        : "bg-slate-900/50 border-slate-700/50 hover:border-slate-600/50"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-white">{team.name}</h4>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {team.age_group && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300">
                              {team.age_group}
                            </span>
                          )}
                          {team.sport_type && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
                              {team.sport_type}
                            </span>
                          )}
                        </div>
                      </div>
                      {team.id === teamId && (
                        <div className="p-1.5 bg-emerald-500 rounded-full">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-slate-400">{t("coach.noTeams", "No teams yet")}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Groups Tab */}
        {activeTab === "groups" && (
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700/50 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-white">{t("coach.groups", "Groups")}</h2>
              </div>
              <button
                onClick={() => setShowGroupModal(true)}
                disabled={!teamId}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t("groups.createGroup", "Create Group")}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.length > 0 ? (
                groups.map((group) => (
                  <div key={group.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-purple-500/50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-white">{group.name}</h4>
                        {group.description && (
                          <p className="text-sm text-slate-400 mt-1 line-clamp-2">{group.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteGroup(group.id)}
                        className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
                        {group._count?.members || 0} {t("groups.members", "members")}
                      </span>
                      <button
                        onClick={() => openManageMembersModal(group)}
                        className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-600 rounded-lg text-sm font-medium transition-colors"
                      >
                        {t("groups.manageMembers", "Manage")}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-700/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="text-slate-400">{t("coach.noGroups", "No groups yet")}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Invite Athlete Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white">{t("invite.inviteAthlete", "Invite Athlete")}</h2>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <form onSubmit={handleSendInvite} className="mb-8">
                <label className="block text-sm font-medium text-slate-300 mb-2">{t("invite.athleteEmail", "Athlete Email")}</label>
                <div className="flex gap-3">
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="athlete@example.com"
                    className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={inviteLoading || !inviteEmail.trim()}
                    className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {inviteLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("common.sending", "Sending...")}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        {t("common.send", "Send")}
                      </>
                    )}
                  </button>
                </div>
              </form>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{t("invite.sentInvitations", "Sent Invitations")}</h3>
                  <span className="text-sm text-slate-400">{teamInvitations.length} invitations</span>
                </div>

                {teamInvitations.length > 0 ? (
                  <div className="space-y-3">
                    {teamInvitations.map((inv) => (
                      <div key={inv.id} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                            <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-medium text-white">{inv.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                inv.status === "pending" ? "bg-amber-500/20 text-amber-300" :
                                inv.status === "accepted" ? "bg-emerald-500/20 text-emerald-300" :
                                "bg-slate-500/20 text-slate-300"
                              }`}>
                                {inv.status}
                              </span>
                              <span className="text-xs text-slate-500">
                                {t("invite.expires", "Expires")}: {formatDate(new Date(inv.expires_at))}
                              </span>
                            </div>
                          </div>
                        </div>
                        {inv.status === "pending" && (
                          <button
                            onClick={() => handleCancelInvite(inv.id)}
                            className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg font-medium transition-colors"
                          >
                            {t("common.cancel", "Cancel")}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-900/50 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-3 bg-slate-700/50 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-slate-400 text-sm">{t("invite.noInvitations", "No invitations sent yet")}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
              >
                {t("common.close", "Close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-white">{t("groups.createGroup", "Create Group")}</h2>
              </div>
              <button
                onClick={() => {
                  setShowGroupModal(false);
                  setGroupForm({ name: "", description: "" });
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateGroup}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t("groups.groupName", "Group Name")} *</label>
                  <input
                    type="text"
                    required
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    placeholder={t("groups.groupNamePlaceholder", "Enter group name")}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t("groups.description", "Description")}</label>
                  <textarea
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    placeholder={t("groups.descriptionPlaceholder", "Optional description")}
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-700 flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowGroupModal(false);
                    setGroupForm({ name: "", description: "" });
                  }}
                  className="flex-1 px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="submit"
                  disabled={saving || !groupForm.name.trim()}
                  className="flex-1 px-6 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t("common.creating", "Creating...") : t("common.create", "Create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Group Members Modal */}
      {showManageMembersModal && selectedGroup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-700">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">{t("groups.manageMembers", "Manage Members")}</h2>
                    <p className="text-sm text-slate-400">{selectedGroup.name}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowManageMembersModal(false);
                  setSelectedGroup(null);
                }}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Current Members */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">{t("groups.currentMembers", "Current Members")}</h3>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-300">
                    {selectedGroup.members?.length || 0} members
                  </span>
                </div>

                {selectedGroup.members && selectedGroup.members.length > 0 ? (
                  <div className="space-y-2">
                    {selectedGroup.members.map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl border border-slate-700/50">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 font-bold text-sm">
                            {(member.athlete.full_name || member.athlete.email).substring(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-white">{member.athlete.full_name || member.athlete.email}</p>
                            <p className="text-xs text-slate-400">{member.athlete.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveMember(selectedGroup.id, member.athlete_id)}
                          className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg font-medium transition-colors"
                        >
                          {t("common.remove", "Remove")}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-900/50 rounded-xl">
                    <p className="text-slate-400 text-sm">{t("groups.noMembers", "No members yet")}</p>
                  </div>
                )}
              </div>

              {/* Add Members */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">{t("groups.addMembers", "Add Members")}</h3>
                {athletes.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {athletes
                      .filter((athlete) => !selectedGroup.members?.some((m) => m.athlete_id === athlete.id))
                      .map((athlete) => (
                        <div key={athlete.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-purple-500/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 font-bold text-sm">
                              {(athlete.full_name || athlete.email).substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-white">{athlete.full_name || athlete.email}</p>
                              <p className="text-xs text-slate-400">{athlete.email}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddMember(selectedGroup.id, athlete.id)}
                            className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium transition-colors"
                          >
                            {t("common.add", "Add")}
                          </button>
                        </div>
                      ))}
                    {athletes.every((athlete) =>
                      selectedGroup.members?.some((m) => m.athlete_id === athlete.id)
                    ) && (
                      <div className="text-center py-6 bg-slate-900/50 rounded-xl">
                        <p className="text-slate-400 text-sm">{t("groups.allAthletesAdded", "All athletes are already in this group")}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-slate-900/50 rounded-xl">
                    <p className="text-slate-400 text-sm">{t("groups.noAthletes", "No athletes available to add")}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-700 flex justify-end">
              <button
                onClick={() => {
                  setShowManageMembersModal(false);
                  setSelectedGroup(null);
                }}
                className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-colors"
              >
                {t("common.close", "Close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
